/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import * as Validator from 'common/utils/Validator';
// Not Utils.generateRandomUUID: Utils reaches the store, breaking slice tests.
import { v4 as uuidv4 } from 'uuid';
import { DocumentContact } from 'common/types';
import {
    DOCUMENT_TRADE_SUB_TYPE,
    QUOTE_TYPE,
    WORK_ORDER_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { ContactFieldValues } from 'admin/components/contacts/contactTypes';

export function initContact(typeId: string = ''): DocumentContact {
    return {
        uuid: uuidv4(),
        typeId,
        name: '',
        email: '',
        mobile: '',
        mobileCountry: 'US',
        phone: '',
        phoneCountry: 'US',
        useLandline: false,
        validMobile: null,
        validPhone: null,
        notifications: [],
        contactPreferenceId: '',
        notes: '',
    };
}

export function hasPrimaryContact(contacts: DocumentContact[] = []): boolean {
    return contacts.some(contact => contact.primaryContact);
}

/** Work orders carry a WORK_ORDER_TYPE here, quotes a QUOTE_TYPE. */
export type DocumentSubTypeId = WORK_ORDER_TYPE | QUOTE_TYPE;

export interface ContactChannelRequirements {
    tradeSubTypeId?: DOCUMENT_TRADE_SUB_TYPE;
    occupied?: boolean;
    documentSubTypeId?: DocumentSubTypeId;
}

// Commercial properties are never occupied.
export function isOccupancyLocked(
    tradeSubTypeId?: DOCUMENT_TRADE_SUB_TYPE
): boolean {
    return tradeSubTypeId === DOCUMENT_TRADE_SUB_TYPE.COMMERCIAL;
}

export function requiresEmailOrMobileChannel(
    requirements?: ContactChannelRequirements
): boolean {
    return (
        requirements?.tradeSubTypeId === DOCUMENT_TRADE_SUB_TYPE.COMMERCIAL ||
        requirements?.documentSubTypeId === WORK_ORDER_TYPE.INSPECTION ||
        !!requirements?.occupied
    );
}

export function isContactValid(
    contact: ContactFieldValues,
    requirements?: ContactChannelRequirements
): boolean {
    const {
        email,
        name,
        mobile,
        notifications,
        phone,
        primaryContact,
        validMobile,
        validPhone,
    } = contact;
    if (!name || !Validator.isMax80Characters(name)) {
        return false;
    }
    if (email && !Validator.isEmail(email)) {
        return false;
    }
    if (mobile && !validMobile) {
        return false;
    }
    if (phone && !validPhone) {
        return false;
    }
    if (!mobile && !email && notifications && notifications.length > 0) {
        return false;
    }
    if (
        primaryContact &&
        requiresEmailOrMobileChannel(requirements) &&
        !email &&
        !mobile
    ) {
        return false;
    }
    return !!(email || mobile || phone);
}
